import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import {
  HiOutlineUpload, HiOutlineTrash, HiOutlineRefresh,
  HiOutlinePencil, HiOutlineSparkles, HiOutlineX, HiOutlineCheck,
  HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineCode
} from 'react-icons/hi';

const SKILL_LABELS = {
  'partner-blog': 'Blog Post',
  'partner-newsletter': 'Newsletter',
  'partner-email-sequence': 'Email Sequence',
  'partner-one-pager': 'One-Pager',
  'partner-banner': 'Banner Ad',
  'partner-service-page': 'Service Page',
  'partner-presentation': 'Presentation',
  'partner-marketing-strategy': 'Marketing Strategy',
  'sales-proposal': 'Sales Proposal',
  'sales-deck': 'Sales Deck',
  'sales-customer-overview': 'Customer Overview',
  'sales-customer-status': 'Customer Status Report',
  'sales-customer-one-pager': 'Customer One-Pager',
  'cs-asset-1': 'CS Asset 1',
  'cs-asset-2': 'CS Asset 2',
  'cs-asset-3': 'CS Asset 3',
  'cs-asset-4': 'CS Asset 4',
};

const SKILL_DASHBOARDS = {
  'partner-blog': ['Partner'],
  'partner-newsletter': ['Partner'],
  'partner-email-sequence': ['Partner'],
  'partner-one-pager': ['Partner'],
  'partner-banner': ['Partner'],
  'partner-service-page': ['Partner'],
  'partner-presentation': ['Partner'],
  'partner-marketing-strategy': ['Partner'],
  'sales-proposal': ['Sales'],
  'sales-deck': ['Sales'],
  'sales-customer-overview': ['Sales'],
  'sales-customer-status': ['Sales'],
  'sales-customer-one-pager': ['Sales'],
  'cs-asset-1': ['CS'],
  'cs-asset-2': ['CS'],
  'cs-asset-3': ['CS'],
  'cs-asset-4': ['CS'],
};

const DASHBOARD_COLORS = {
  Partner: 'bg-blue-100 text-blue-700',
  Sales: 'bg-amber-100 text-amber-700',
  CS: 'bg-teal-100 text-teal-700',
};

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4' },
  { value: 'claude-haiku-35-20241022', label: 'Claude Haiku 3.5' },
];

export default function AdminLibrary() {
  const [files, setFiles] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncModel, setSyncModel] = useState('claude-sonnet-4-20250514');
  const [syncStatus, setSyncStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('library');
  const fileInputRef = useRef();
  const { addToast } = useToast();

  // Skills state
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [savingSkill, setSavingSkill] = useState(false);
  const [feedbackSkill, setFeedbackSkill] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackModel, setFeedbackModel] = useState('claude-sonnet-4-20250514');
  const [regenerating, setRegenerating] = useState(null);
  const [uploadingSkill, setUploadingSkill] = useState(null);
  const [syncReport, setSyncReport] = useState(null);
  const [showSyncReport, setShowSyncReport] = useState(false);
  const [expandedReportSkill, setExpandedReportSkill] = useState(null);
  const skillFileRefs = useRef({});

  const loadData = async () => {
    try {
      const [filesRes, syncRes, skillsRes] = await Promise.all([
        api.get('/library'),
        api.get('/library/sync-status'),
        api.get('/library/skills'),
      ]);
      setFiles(filesRes.data);
      setSyncStatus(syncRes.data);
      setSkills(skillsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleUpload = async (e) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(uploadFiles).forEach(f => formData.append('files', f));
      await api.post('/library/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      addToast(`${uploadFiles.length} file(s) uploaded`);
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/library/${id}`);
      addToast('File deleted');
      loadData();
    } catch (err) {
      addToast('Delete failed', 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/library/sync', { model: syncModel });
      addToast(data.message);
      if (data.report) {
        setSyncReport(data);
        setShowSyncReport(true);
        setExpandedReportSkill(null);
      }
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Skills handlers
  const startEditing = (skill) => {
    setEditingSkill(skill.id);
    setEditContent(skill.content);
    setFeedbackSkill(null);
  };

  const cancelEditing = () => {
    setEditingSkill(null);
    setEditContent('');
  };

  const saveSkill = async () => {
    setSavingSkill(true);
    try {
      await api.put(`/library/skills/${editingSkill}`, { content: editContent });
      addToast('Skill updated successfully');
      setEditingSkill(null);
      setEditContent('');
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save skill', 'error');
    }
    setSavingSkill(false);
  };

  const startFeedback = (skill) => {
    setFeedbackSkill(skill.id);
    setFeedbackText('');
    setFeedbackModel('claude-sonnet-4-20250514');
    setEditingSkill(null);
  };

  const submitFeedback = async (skillId) => {
    if (!feedbackText.trim()) {
      addToast('Please enter feedback', 'error');
      return;
    }
    setRegenerating(skillId);
    try {
      const { data } = await api.post(`/library/skills/${skillId}/regenerate`, { feedback: feedbackText, model: feedbackModel });
      addToast(data.message);
      setFeedbackSkill(null);
      setFeedbackText('');
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to regenerate skill', 'error');
    }
    setRegenerating(null);
  };

  const handleSkillUpload = async (skillId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSkill(skillId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/library/skills/${skillId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      addToast(data.message);
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to upload skill', 'error');
    } finally {
      setUploadingSkill(null);
      if (skillFileRefs.current[skillId]) skillFileRefs.current[skillId].value = '';
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Library & Skills</h1>
          <p className="text-gray-500">Reference materials and generation skills</p>
        </div>
        {activeTab === 'library' && (
          <div className="flex items-center gap-3">
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              <HiOutlineUpload className="w-5 h-5" /> {uploading ? 'Uploading...' : 'Upload Files'}
            </button>
            <select
              value={syncModel}
              onChange={(e) => setSyncModel(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-2.5 py-2 bg-white focus:ring-2 focus:ring-v2-blue focus:border-transparent"
            >
              {MODEL_OPTIONS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <button onClick={handleSync} disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50">
              <HiOutlineRefresh className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync Skills'}
            </button>
          </div>
        )}
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.md" onChange={handleUpload} className="hidden" />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('library')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'library'
              ? 'border-v2-blue text-v2-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Reference Materials ({files.length})
        </button>
        <button
          onClick={() => setActiveTab('skills')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'skills'
              ? 'border-v2-blue text-v2-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Generation Skills ({skills.length})
        </button>
      </div>

      {/* Library Tab */}
      {activeTab === 'library' && (
        <>
          {/* Sync status */}
          {syncStatus && (
            <div className={`p-3 rounded-lg mb-4 text-sm ${
              syncStatus.status === 'success' ? 'bg-green-50 text-green-800' :
              syncStatus.status === 'failed' ? 'bg-red-50 text-red-800' :
              'bg-gray-50 text-gray-600'
            }`}>
              Last sync: {syncStatus.syncedAt ? new Date(syncStatus.syncedAt).toLocaleString() : 'Never'} — {syncStatus.details || syncStatus.status}
            </div>
          )}

          {/* Files list */}
          {files.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
              No library files yet. Upload PDFs, DOCX, TXT, or MD files.
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Filename</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Description</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Uploaded</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {files.map(file => (
                    <tr key={file.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{file.filename}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 uppercase">{file.fileType}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{file.description || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(file.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleDelete(file.id)} className="text-gray-400 hover:text-red-600">
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Skills Tab */}
      {activeTab === 'skills' && (
        <div className="space-y-3">
          {skills.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
              No skills found in the skills directory.
            </div>
          ) : (
            skills.map(skill => (
              <div key={skill.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Skill header */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSkill(expandedSkill === skill.id ? null : skill.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <HiOutlineCode className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {SKILL_LABELS[skill.id] || skill.name}
                        </h3>
                        {(SKILL_DASHBOARDS[skill.id] || []).map(db => (
                          <span key={db} className={`text-xs font-medium px-2 py-0.5 rounded-full ${DASHBOARD_COLORS[db] || 'bg-gray-100 text-gray-600'}`}>
                            {db}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1">{skill.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {skill.hasLibraryContext && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Synced</span>
                    )}
                    {expandedSkill === skill.id ? (
                      <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded content */}
                {expandedSkill === skill.id && (
                  <div className="border-t border-gray-200 px-5 py-4">
                    {/* Action buttons */}
                    {editingSkill !== skill.id && feedbackSkill !== skill.id && (
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditing(skill); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                        >
                          <HiOutlinePencil className="w-4 h-4" /> Edit Directly
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); startFeedback(skill); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-300 rounded-lg hover:bg-purple-50 text-purple-700"
                        >
                          <HiOutlineSparkles className="w-4 h-4" /> Update with AI
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); skillFileRefs.current[skill.id]?.click(); }}
                          disabled={uploadingSkill === skill.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-orange-300 rounded-lg hover:bg-orange-50 text-orange-700 disabled:opacity-50"
                        >
                          <HiOutlineUpload className="w-4 h-4" />
                          {uploadingSkill === skill.id ? 'Uploading...' : 'Upload Replacement'}
                        </button>
                        <input
                          ref={el => skillFileRefs.current[skill.id] = el}
                          type="file"
                          accept=".md,.txt"
                          onChange={(e) => handleSkillUpload(skill.id, e)}
                          className="hidden"
                        />
                      </div>
                    )}

                    {/* Edit mode */}
                    {editingSkill === skill.id ? (
                      <div>
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full h-96 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-y"
                          placeholder="SKILL.md content..."
                        />
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={saveSkill}
                            disabled={savingSkill}
                            className="flex items-center gap-1.5 px-4 py-2 bg-v2-blue text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingSkill ? (
                              <><HiOutlineRefresh className="w-4 h-4 animate-spin" /> Saving...</>
                            ) : (
                              <><HiOutlineCheck className="w-4 h-4" /> Save Changes</>
                            )}
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                          >
                            <HiOutlineX className="w-4 h-4" /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : feedbackSkill === skill.id ? (
                      /* Feedback / AI regeneration mode */
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Describe what you want to change
                        </label>
                        <textarea
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y"
                          placeholder="e.g., Make the blog posts more technical and include more statistics. Add a section about security compliance. Change the minimum word count to 2,000..."
                        />
                        <div className="flex items-center gap-3 mt-3">
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 whitespace-nowrap">Model:</label>
                            <select
                              value={feedbackModel}
                              onChange={(e) => setFeedbackModel(e.target.value)}
                              className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                              <option value="claude-opus-4-20250514">Claude Opus 4</option>
                              <option value="claude-haiku-35-20241022">Claude Haiku 3.5</option>
                            </select>
                          </div>
                          <button
                            onClick={() => submitFeedback(skill.id)}
                            disabled={regenerating === skill.id || !feedbackText.trim()}
                            className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                          >
                            {regenerating === skill.id ? (
                              <><HiOutlineRefresh className="w-4 h-4 animate-spin" /> Regenerating...</>
                            ) : (
                              <><HiOutlineSparkles className="w-4 h-4" /> Update Skill with AI</>
                            )}
                          </button>
                          <button
                            onClick={() => { setFeedbackSkill(null); setFeedbackText(''); }}
                            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                          >
                            <HiOutlineX className="w-4 h-4" /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Read-only preview */
                      <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">
                        {skill.content || 'No SKILL.md file found.'}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Sync Report Modal */}
      {showSyncReport && syncReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Modal header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Sync Complete</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {syncReport.filesProcessed} file{syncReport.filesProcessed !== 1 ? 's' : ''} synced to {syncReport.skillsUpdated} skill{syncReport.skillsUpdated !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setShowSyncReport(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            {/* Summary stats */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex gap-4">
              {[
                { label: 'Created', count: syncReport.report.filter(r => r.status === 'created').length, color: 'text-green-600 bg-green-100' },
                { label: 'Updated', count: syncReport.report.filter(r => r.status === 'updated').length, color: 'text-blue-600 bg-blue-100' },
                { label: 'Unchanged', count: syncReport.report.filter(r => r.status === 'unchanged').length, color: 'text-gray-600 bg-gray-200' },
              ].map(stat => (
                <div key={stat.label} className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stat.color}`}>{stat.count}</span>
                  <span className="text-sm text-gray-600">{stat.label}</span>
                </div>
              ))}
            </div>

            {/* Accordion list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
              {syncReport.report.map(item => {
                const isExpanded = expandedReportSkill === item.skillId;
                const lineDelta = item.newLines - item.previousLines;
                const sizeDelta = item.newSize - item.previousSize;
                const statusBadge = {
                  created: { text: 'Created', className: 'bg-green-100 text-green-700' },
                  updated: { text: 'Updated', className: 'bg-blue-100 text-blue-700' },
                  unchanged: { text: 'Unchanged', className: 'bg-gray-100 text-gray-500' },
                }[item.status];

                return (
                  <div key={item.skillId} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedReportSkill(isExpanded ? null : item.skillId)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{item.skillName}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge.className}`}>
                          {statusBadge.text}
                        </span>
                        {item.status !== 'unchanged' && lineDelta !== 0 && (
                          <span className={`text-xs ${lineDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {lineDelta > 0 ? '+' : ''}{lineDelta} lines
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <HiOutlineChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <HiOutlineChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-2">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Previous:</span>{' '}
                            <span className="font-medium">{item.previousLines > 0 ? `${item.previousLines} lines (${(item.previousSize / 1024).toFixed(1)} KB)` : 'None'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">New:</span>{' '}
                            <span className="font-medium">{item.newLines} lines ({(item.newSize / 1024).toFixed(1)} KB)</span>
                          </div>
                        </div>
                        {sizeDelta !== 0 && (
                          <div className="text-sm">
                            <span className="text-gray-500">Size change:</span>{' '}
                            <span className={`font-medium ${sizeDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {sizeDelta > 0 ? '+' : ''}{(sizeDelta / 1024).toFixed(1)} KB
                            </span>
                          </div>
                        )}
                        {item.filesIncluded?.length > 0 && (
                          <div className="text-sm">
                            <span className="text-gray-500">Library files included:</span>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {item.filesIncluded.map((f, i) => (
                                <span key={i} className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded">
                                  {f}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowSyncReport(false)}
                className="px-4 py-2 bg-v2-blue text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
